import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../components/Icon';
import ConfirmDialog from '../components/ConfirmDialog';
import { useToast } from '../components/Toast';
import {
  Listing,
  ListingStatus,
  ListingType,
  deleteListing,
  listMyListings,
  listingTypeLabel,
} from '../api/listings';
import { extractApiError } from '../api/legalServices';

type Filter = 'All' | 'Rental' | 'Sale' | 'Active' | 'Inactive';
const FILTERS: Filter[] = ['All', 'Rental', 'Sale', 'Active', 'Inactive'];

type ViewMode = 'grid' | 'list';

const fmt = (n: number): string => n.toLocaleString();

const statusLabel = (status: ListingStatus): string =>
  status.charAt(0).toUpperCase() + status.slice(1);

const statusColor = (status: ListingStatus): string =>
  status === 'active' ? 'var(--green)' : 'var(--g400)';

// Fallback image when a listing has no photo yet.
const PLACEHOLDER_IMG =
  'https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=400&h=260&fit=crop';

const photoFor = (l: Listing): string => {
  // photos may hold real URLs (preferred) or labels from the demo form.
  const first = l.coverPhoto || (Array.isArray(l.photos) ? l.photos[0] : null);
  if (!first) return PLACEHOLDER_IMG;
  if (/^https?:\/\//i.test(first)) return first;
  return PLACEHOLDER_IMG;
};

const priceNumber = (p: Listing['price']): number => {
  const n = typeof p === 'number' ? p : Number(p);
  return Number.isFinite(n) ? n : 0;
};

const AgentListings: React.FC = () => {
  const navigate = useNavigate();
  const { toast } = useToast();

  const [listings, setListings] = useState<Listing[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>('All');
  const [view, setView] = useState<ViewMode>('grid');
  const [pendingDelete, setPendingDelete] = useState<Listing | null>(null);
  const [deleting, setDeleting] = useState<boolean>(false);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      // Pull both statuses so the user sees inactive listings too — the
      // filter chips narrow it down on the client.
      const res = await listMyListings({ limit: 100 });
      setListings(res.listings);
    } catch (err) {
      setLoadError(extractApiError(err, 'Failed to load listings'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo<Listing[]>(() => {
    if (filter === 'All') return listings;
    if (filter === 'Rental' || filter === 'Sale') {
      return listings.filter((l) => l.type === filter);
    }
    // 'Active' / 'Inactive' — compare against the lowercase backend value.
    const wanted = filter.toLowerCase() as ListingStatus;
    return listings.filter((l) => l.status === wanted);
  }, [listings, filter]);

  const requestDelete = (l: Listing) => setPendingDelete(l);

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    const target = pendingDelete;
    setDeleting(true);
    try {
      await deleteListing(target.id);
      setListings((prev) => prev.filter((l) => l.id !== target.id));
      toast(`"${target.title}" deleted`, 'info');
      setPendingDelete(null);
    } catch (err) {
      toast(extractApiError(err, 'Delete failed'), 'error');
    } finally {
      setDeleting(false);
    }
  };

  const empty = !loading && !loadError && filtered.length === 0;

  return (
    <div>
      {/* Top bar: filters + view toggle + add button */}
      <div className="flex justify-between items-center gap-3 flex-wrap mb-5">
        <div className="flex gap-1.5 items-center w-full overflow-x-auto no-scrollbar sm:w-auto sm:flex-wrap sm:overflow-visible">
          {FILTERS.map((f) => {
            const active = filter === f;
            return (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className="capitalize flex-shrink-0 whitespace-nowrap"
                style={{
                  padding: '7px 16px',
                  borderRadius: 8,
                  fontSize: 13,
                  fontWeight: active ? 600 : 500,
                  border:
                    '1px solid ' + (active ? 'var(--brand)' : 'var(--g200)'),
                  background: active ? 'var(--brand-light)' : '#fff',
                  color: active ? 'var(--brand)' : 'var(--g600)',
                  cursor: 'pointer',
                  transition: 'all .15s ease',
                }}
              >
                {listingTypeLabel(f)}
              </button>
            );
          })}
        </div>

        <div className="flex gap-2 items-center justify-between w-full sm:w-auto">
          <div
            className="flex overflow-hidden"
            style={{ border: '1px solid var(--g200)', borderRadius: 8 }}
          >
            <button
              onClick={() => setView('grid')}
              style={{
                padding: '7px 10px',
                background: view === 'grid' ? 'var(--g100)' : '#fff',
                color: view === 'grid' ? 'var(--ink)' : 'var(--g400)',
                borderRight: '1px solid var(--g200)',
                cursor: 'pointer',
              }}
              aria-label="Grid view"
            >
              <Icon
                name="layout-dashboard"
                size={16}
                color={view === 'grid' ? 'var(--ink)' : 'var(--g400)'}
              />
            </button>
            <button
              onClick={() => setView('list')}
              style={{
                padding: '7px 10px',
                background: view === 'list' ? 'var(--g100)' : '#fff',
                color: view === 'list' ? 'var(--ink)' : 'var(--g400)',
                cursor: 'pointer',
              }}
              aria-label="List view"
            >
              <Icon
                name="bar-chart-3"
                size={16}
                color={view === 'list' ? 'var(--ink)' : 'var(--g400)'}
              />
            </button>
          </div>

          <button
            className="btn btn-primary btn-sm"
            onClick={() => navigate('/agent/listings/new')}
          >
            <Icon name="plus" size={16} color="#fff" /> Add Listing
          </button>
        </div>
      </div>

      {/* Loading */}
      {loading && (
        <div className="card text-center py-12 text-g500">
          <Icon name="building-2" size={28} color="var(--g400)" /> Loading
          listings...
        </div>
      )}

      {/* Error */}
      {loadError && (
        <div
          className="card p-5"
          role="alert"
          style={{
            background: 'var(--red-bg)',
            color: 'var(--red)',
            border: '1px solid rgba(220,38,38,.12)',
          }}
        >
          <div className="flex items-center gap-2 mb-2">
            <Icon name="x-circle" size={18} color="var(--red)" />
            <strong>Couldn't load listings</strong>
          </div>
          <div className="text-sm mb-3">{loadError}</div>
          <button className="btn btn-secondary btn-sm" onClick={load}>
            Try again
          </button>
        </div>
      )}

      {/* Empty state */}
      {empty && (
        <div
          className="card flex flex-col items-center text-center animate-fadeIn px-5 py-12 sm:px-8 sm:py-16"
          style={{ borderRadius: 16 }}
        >
          <div
            className="rounded-[20px] flex items-center justify-center mb-5"
            style={{ width: 80, height: 80, background: 'var(--brand-bg)' }}
          >
            <Icon name="building-2" size={38} color="var(--brand)" />
          </div>
          <h3
            className="font-extrabold text-ink mb-2"
            style={{ fontSize: 20, letterSpacing: '-0.3px' }}
          >
            {filter === 'All' ? 'No listings yet' : `No ${filter.toLowerCase()} listings`}
          </h3>
          <p
            className="text-g500 leading-relaxed mb-5"
            style={{ fontSize: 14, maxWidth: 440 }}
          >
            {filter === 'All'
              ? 'Start by adding your first property listing. It only takes a few minutes to get your property in front of potential tenants and buyers.'
              : 'Try a different filter, or add a new listing.'}
          </p>
          <button
            className="btn btn-primary"
            onClick={() => navigate('/agent/listings/new')}
          >
            <Icon name="plus" size={18} color="#fff" /> Add Your First Listing
          </button>
        </div>
      )}

      {/* Grid view */}
      {!loading && !loadError && filtered.length > 0 && view === 'grid' && (
        <div
          className="grid gap-4 sm:gap-[18px]"
          style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(min(300px, 100%), 1fr))' }}
        >
          {filtered.map((l) => {
            const price = priceNumber(l.price);
            return (
              <div
                key={l.id}
                className="overflow-hidden animate-fadeIn"
                role="button"
                tabIndex={0}
                onClick={() => navigate(`/agent/listings/${l.id}`)}
                onKeyDown={(e) => {
                  // Only react to keys on the card itself — not on Enter/Space
                  // bubbling up from the Edit/Delete buttons inside it, which
                  // would otherwise hijack their keyboard activation.
                  if (e.target !== e.currentTarget) return;
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    navigate(`/agent/listings/${l.id}`);
                  }
                }}
                style={{
                  background: '#fff',
                  borderRadius: 16,
                  border: '1px solid var(--g100)',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
                  transition: 'box-shadow 0.2s, transform 0.2s',
                  cursor: 'pointer',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.boxShadow =
                    '0 4px 16px rgba(0,0,0,0.08)';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.boxShadow =
                    '0 1px 2px rgba(0,0,0,0.04)';
                  e.currentTarget.style.transform = 'none';
                }}
              >
                <div style={{ position: 'relative', overflow: 'hidden' }}>
                  <img
                    src={photoFor(l)}
                    alt={l.title}
                    style={{
                      width: '100%',
                      height: 185,
                      objectFit: 'cover',
                      display: 'block',
                    }}
                  />
                  <div
                    style={{
                      position: 'absolute',
                      top: 12,
                      left: 12,
                      display: 'flex',
                      gap: 6,
                    }}
                  >
                    <span
                      className="font-semibold"
                      style={{
                        padding: '3px 10px',
                        borderRadius: 100,
                        fontSize: 11.5,
                        background:
                          l.type === 'Rental' ? 'var(--brand)' : 'var(--blue)',
                        color: '#fff',
                      }}
                    >
                      {listingTypeLabel(l.type)}
                    </span>
                  </div>
                  <div
                    style={{
                      position: 'absolute',
                      top: 12,
                      right: 12,
                      background: 'rgba(255,255,255,0.92)',
                      padding: '3px 10px',
                      borderRadius: 100,
                      backdropFilter: 'blur(4px)',
                    }}
                  >
                    <span
                      className="font-semibold flex items-center gap-1.5"
                      style={{ fontSize: 12, color: statusColor(l.status) }}
                    >
                      <span
                        style={{
                          width: 7,
                          height: 7,
                          borderRadius: '50%',
                          background: statusColor(l.status),
                        }}
                      />
                      {statusLabel(l.status)}
                    </span>
                  </div>
                </div>

                <div style={{ padding: '16px 18px 18px' }}>
                  <div
                    className="font-bold text-ink mb-1 truncate"
                    style={{ fontSize: 15, letterSpacing: '-0.2px' }}
                  >
                    {l.title}
                  </div>
                  <div
                    className="flex items-center gap-1 text-g400 mb-3 min-w-0"
                    style={{ fontSize: 12.5 }}
                  >
                    <Icon name="map-pin" size={13} color="var(--g400)" className="flex-shrink-0" />
                    <span className="truncate">{l.address}</span>
                  </div>
                  <div className="flex gap-3.5 mb-3.5 flex-wrap">
                    {l.beds > 0 && (
                      <span
                        className="flex items-center gap-1 text-g500"
                        style={{ fontSize: 12 }}
                      >
                        <Icon name="home" size={14} color="var(--g500)" />
                        {l.beds} Beds
                      </span>
                    )}
                    <span
                      className="flex items-center gap-1 text-g500"
                      style={{ fontSize: 12 }}
                    >
                      <Icon name="package" size={14} color="var(--g500)" />
                      {l.baths} Bath
                    </span>
                    {l.sqft != null && l.sqft > 0 && (
                      <span
                        className="flex items-center gap-1 text-g500"
                        style={{ fontSize: 12 }}
                      >
                        <Icon
                          name="bar-chart-3"
                          size={14}
                          color="var(--g500)"
                        />
                        {fmt(l.sqft)} sqft
                      </span>
                    )}
                  </div>
                  <div
                    className="flex justify-between items-center"
                    style={{
                      paddingTop: 12,
                      borderTop: '1px solid var(--g100)',
                    }}
                  >
                    <span
                      className="font-extrabold text-ink"
                      style={{ fontSize: 18, letterSpacing: '-0.3px' }}
                    >
                      ${fmt(price)}
                      {l.type === 'Rental' && (
                        <span
                          className="font-normal text-g400"
                          style={{ fontSize: 13 }}
                        >
                          /mo
                        </span>
                      )}
                    </span>
                    <div className="flex gap-1.5">
                      <button
                        style={{
                          padding: 6,
                          borderRadius: 8,
                          border: '1px solid var(--g200)',
                          background: '#fff',
                          cursor: 'pointer',
                        }}
                        aria-label="Edit"
                        title="Edit listing"
                        onClick={(e) => { e.stopPropagation(); navigate(`/agent/listings/${l.id}/edit`); }}
                      >
                        <Icon name="pencil" size={14} color="var(--g500)" />
                      </button>
                      <button
                        style={{
                          padding: 6,
                          borderRadius: 8,
                          border: '1px solid var(--g200)',
                          background: '#fff',
                          cursor: 'pointer',
                        }}
                        aria-label="Delete"
                        onClick={(e) => { e.stopPropagation(); requestDelete(l); }}
                      >
                        <Icon name="x-circle" size={14} color="var(--red)" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* List view */}
      {!loading && !loadError && filtered.length > 0 && view === 'list' && (
        <div className="flex flex-col gap-2">
          {filtered.map((l) => {
            const price = priceNumber(l.price);
            return (
              <div
                key={l.id}
                className="flex items-center gap-3 md:gap-4 animate-fadeIn p-3 sm:px-[18px] sm:py-[14px]"
                role="button"
                tabIndex={0}
                onClick={() => navigate(`/agent/listings/${l.id}`)}
                onKeyDown={(e) => {
                  // Only react to keys on the card itself — not on Enter/Space
                  // bubbling up from the Edit/Delete buttons inside it, which
                  // would otherwise hijack their keyboard activation.
                  if (e.target !== e.currentTarget) return;
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    navigate(`/agent/listings/${l.id}`);
                  }
                }}
                style={{
                  background: '#fff',
                  borderRadius: 12,
                  border: '1px solid var(--g100)',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
                  transition: 'box-shadow .15s',
                  cursor: 'pointer',
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.boxShadow =
                    '0 1px 4px rgba(0,0,0,0.06)')
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.boxShadow =
                    '0 1px 2px rgba(0,0,0,0.04)')
                }
              >
                <img
                  src={photoFor(l)}
                  alt={l.title}
                  className="w-16 h-14 sm:w-20 sm:h-[58px]"
                  style={{
                    borderRadius: 10,
                    objectFit: 'cover',
                    flexShrink: 0,
                  }}
                />
                <div className="flex-1 min-w-0">
                  <div
                    className="font-bold text-ink truncate"
                    style={{ fontSize: 14 }}
                  >
                    {l.title}
                  </div>
                  <div
                    className="flex items-center gap-1 text-g400 min-w-0"
                    style={{ fontSize: 12 }}
                  >
                    <Icon name="map-pin" size={12} color="var(--g400)" className="flex-shrink-0" />
                    <span className="truncate">{l.address}</span>
                  </div>
                  {/* Price, status and type collapse under the title on phones. */}
                  <div
                    className="md:hidden flex items-center gap-x-2.5 gap-y-1 flex-wrap mt-1"
                    style={{ fontSize: 12 }}
                  >
                    <span className="font-bold text-ink" style={{ fontSize: 13 }}>
                      ${fmt(price)}
                      {l.type === 'Rental' && (
                        <span className="font-normal text-g400">/mo</span>
                      )}
                    </span>
                    <span
                      className="flex items-center gap-1 font-medium"
                      style={{ color: statusColor(l.status) }}
                    >
                      <span
                        style={{
                          width: 6,
                          height: 6,
                          borderRadius: '50%',
                          background: statusColor(l.status),
                        }}
                      />
                      {statusLabel(l.status)}
                    </span>
                    <span
                      className="font-semibold"
                      style={{
                        color: l.type === 'Rental' ? 'var(--brand)' : 'var(--blue)',
                      }}
                    >
                      {listingTypeLabel(l.type)}
                    </span>
                  </div>
                </div>
                <span
                  className="font-semibold hidden md:inline-block"
                  style={{
                    padding: '3px 10px',
                    borderRadius: 100,
                    fontSize: 11.5,
                    background:
                      l.type === 'Rental' ? 'var(--brand)' : 'var(--blue)',
                    color: '#fff',
                  }}
                >
                  {listingTypeLabel(l.type)}
                </span>
                <span
                  className="hidden md:flex items-center gap-1.5 font-medium"
                  style={{
                    fontSize: 12.5,
                    color: statusColor(l.status),
                    minWidth: 70,
                  }}
                >
                  <span
                    style={{
                      width: 7,
                      height: 7,
                      borderRadius: '50%',
                      background: statusColor(l.status),
                    }}
                  />
                  {statusLabel(l.status)}
                </span>
                <span
                  className="font-bold text-ink hidden md:inline"
                  style={{ fontSize: 14, minWidth: 100, textAlign: 'right' }}
                >
                  ${fmt(price)}
                  {l.type === 'Rental' && (
                    <span
                      className="font-normal text-g400"
                      style={{ fontSize: 12 }}
                    >
                      /mo
                    </span>
                  )}
                </span>
                <div className="flex flex-col sm:flex-row gap-1.5 flex-shrink-0">
                  <button
                    style={{
                      padding: 7,
                      borderRadius: 8,
                      border: '1px solid var(--g200)',
                      background: '#fff',
                      cursor: 'pointer',
                    }}
                    aria-label="Edit"
                    title="Edit listing"
                    onClick={(e) => { e.stopPropagation(); navigate(`/agent/listings/${l.id}/edit`); }}
                  >
                    <Icon name="pencil" size={14} color="var(--g500)" />
                  </button>
                  <button
                    style={{
                      padding: 7,
                      borderRadius: 8,
                      border: '1px solid var(--g200)',
                      background: '#fff',
                      cursor: 'pointer',
                    }}
                    aria-label="Delete"
                    onClick={(e) => { e.stopPropagation(); requestDelete(l); }}
                  >
                    <Icon name="x-circle" size={14} color="var(--red)" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Delete confirmation */}
      <ConfirmDialog
        open={pendingDelete !== null}
        title="Delete listing"
        variant="danger"
        confirmLabel="Delete"
        cancelLabel="Keep"
        busy={deleting}
        message={
          <>
            Are you sure you want to permanently delete{' '}
            <strong style={{ color: 'var(--ink)' }}>
              {pendingDelete?.title}
            </strong>
            ?
            <br />
            This cannot be undone.
          </>
        }
        onConfirm={confirmDelete}
        onCancel={() => {
          if (!deleting) setPendingDelete(null);
        }}
      />
    </div>
  );
};

// Re-exporting these in case other files were importing the old local types.
export type { ListingType, ListingStatus };

export default AgentListings;
